import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaReceipt, FaTimes } from "react-icons/fa";
import { confirmAttachment } from "../../features/attachments/attachmentSlice";

let rowKeySeq = 0;
const nextRowKey = () => rowKeySeq++;

const today = () => new Date().toISOString().split("T")[0];

// Builds <option>/<optgroup> for the leaf (selectable) categories only,
// grouped under their parent's name - mirrors SelectSearch's parent/child
// grouping without pulling react-select into a per-row table cell.
const buildCategoryGroups = (categories) => {
  const parents = new Map();
  (categories || []).forEach((cat) => {
    if (cat.parent === null || cat.parent === undefined) {
      parents.set(cat._id, { name: cat.name, children: [] });
    }
  });
  (categories || []).forEach((cat) => {
    if (cat.parent && parents.has(cat.parent)) {
      parents.get(cat.parent).children.push(cat);
    }
  });
  return [...parents.values()].filter((group) => group.children.length > 0);
};

const CLASSIFICATION_COPY = {
  multiple: {
    title: "Multiple transactions detected",
    subtitle: "This looks like a statement with several transactions - review each one below.",
  },
  single: {
    title: "Receipt scanned",
    subtitle: "Review the detected details before adding it as an expense.",
  },
  unknown: {
    title: "File saved",
    subtitle: "We couldn't automatically read this file.",
  },
};

/**
 * ReceiptReviewModal - shown after DropzoneCard's Upload triggers a
 * scan (POST .../attachments/scan). The file is already saved server-side
 * at that point; this is just a review/edit step before turning the
 * OCR-parsed (best-effort, often imperfect) entries into real notaSpese via
 * POST .../attachments/:id/confirm.
 */
const ReceiptReviewModal = ({ show, onHide, scanResult, schedaId, categories }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!scanResult) {
      setRows([]);
      return;
    }
    setRows(
      (scanResult.entries || []).map((entry) => ({
        key: nextRowKey(),
        testo: entry.testo || "",
        importo: entry.importo != null ? String(entry.importo) : "",
        inserimentoData: entry.inserimentoData || today(),
        categoria: "",
      }))
    );
  }, [scanResult]);

  if (!scanResult) return null;

  const classification = scanResult.classification || "unknown";
  const copy = CLASSIFICATION_COPY[classification] || CLASSIFICATION_COPY.unknown;
  const categoryGroups = buildCategoryGroups(categories);

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const validRows = rows.filter(
    (row) => row.testo.trim() && row.importo !== "" && !Number.isNaN(parseFloat(row.importo))
  );

  const handleConfirm = async () => {
    if (validRows.length === 0) return;
    setIsSubmitting(true);
    try {
      const entries = validRows.map((row) => ({
        testo: row.testo.trim(),
        importo: parseFloat(row.importo),
        inserimentoData: row.inserimentoData,
        categoria: row.categoria || null,
      }));
      const response = await dispatch(
        confirmAttachment({ schedaId, attachmentId: scanResult.attachmentId, entries })
      ).unwrap();
      toast.success(
        `${response.notaSpese.length} expense${response.notaSpese.length === 1 ? "" : "s"} added`
      );
      onHide(true);
    } catch (error) {
      toast.error(`Error adding expenses: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasEntries = (scanResult.entries || []).length > 0;

  return (
    <Modal show={show} onHide={() => onHide(false)} centered size={hasEntries ? "lg" : undefined} contentClassName="note-modal-content">
      <Modal.Header closeButton className="note-modal-header">
        <div className="d-flex align-items-center gap-3">
          <div className="note-modal-icon">
            <FaReceipt size={15} />
          </div>
          <div>
            <Modal.Title className="note-modal-title">{copy.title}</Modal.Title>
            <p className="note-modal-subtitle mb-0">
              {scanResult.filename ? `${scanResult.filename} - ` : ""}
              {copy.subtitle}
            </p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="note-modal-body">
        {!hasEntries ? (
          <p className="mb-0">
            The file is saved as an attachment on this scheda either way. Add matching
            expense(s) manually with &ldquo;Add note&rdquo; if needed.
          </p>
        ) : (
          <>
            <p className="receipt-review-hint mb-3">
              The image is already saved - these fields are just a best-effort read, edit
              anything that's wrong (or remove a row) before adding.
            </p>
            <div className="receipt-review-rows">
              {rows.map((row) => (
                <div className="receipt-review-row" key={row.key}>
                  <input
                    type="date"
                    className="form-control receipt-review-date"
                    value={row.inserimentoData}
                    onChange={(e) => updateRow(row.key, "inserimentoData", e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control receipt-review-testo"
                    value={row.testo}
                    placeholder="Description"
                    maxLength={100}
                    onChange={(e) => updateRow(row.key, "testo", e.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    className="form-control receipt-review-importo"
                    value={row.importo}
                    placeholder="Amount"
                    onChange={(e) => updateRow(row.key, "importo", e.target.value)}
                  />
                  <select
                    className="form-select receipt-review-categoria"
                    value={row.categoria}
                    onChange={(e) => updateRow(row.key, "categoria", e.target.value)}
                  >
                    <option value="">No category</option>
                    {categoryGroups.map((group) => (
                      <optgroup key={group.name} label={group.name}>
                        {group.children.map((child) => (
                          <option key={child._id} value={child._id}>
                            {child.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="receipt-review-remove"
                    aria-label="Remove this entry"
                    onClick={() => removeRow(row.key)}
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              ))}
              {rows.length === 0 && (
                <p className="text-muted mb-0">All entries removed - nothing to add.</p>
              )}
            </div>
          </>
        )}

        <div className="row mt-4">
          <div className="col-12">
            {hasEntries ? (
              <button
                type="button"
                className="d-flex align-items-center justify-content-center btn bg-gradient-dark w-100"
                disabled={validRows.length === 0 || isSubmitting}
                onClick={handleConfirm}
              >
                <p className="mb-0">
                  {isSubmitting
                    ? "Adding..."
                    : `Add ${validRows.length} expense${validRows.length === 1 ? "" : "s"}`}
                </p>
              </button>
            ) : (
              <button
                type="button"
                className="btn dashboard-btn-secondary w-100"
                onClick={() => onHide(false)}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ReceiptReviewModal;
